/**
 * Image History Popover -- Grid popover for browsing image generation history.
 *
 * Rendered inside the node's DOM subtree so it zooms/pans with the canvas.
 * Uses nopan/nodrag CSS classes to prevent ReactFlow from intercepting interactions.
 *
 * Supports batch_pending: when > 0, renders spinner placeholder cells for
 * pending generations that haven't completed yet. Spinner cells appear at
 * the front (newest-first order) and are not selectable.
 *
 * Each cell shows download and fullscreen buttons on hover (top-left).
 * Clicking the image itself does NOT select it -- only the "Choose vN" button does.
 * The selected version shows a large yellow star.
 */

import { memo, useState, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import { getThumbnailUrl, getAssetUrl, authenticatedFetch } from '@ponpon/api-client'
import type { HistoryEntry } from './types'

interface ImageHistoryPopoverProps {
  projectId: string
  history: HistoryEntry[]
  pickedIndex: number
  onSelect: (index: number) => void
  isOpen: boolean
  thumbnailWidth: number
  cacheKey?: string
  batchPending?: number
  cellAspectRatio?: string
}

const PendingCell = memo(function PendingCell({
  aspectRatio,
}: {
  aspectRatio: string
}) {
  return (
    <div
      data-testid="popover-pending-item"
      className="relative rounded overflow-hidden border-2 border-yellow-400/30 bg-black/20 flex items-center justify-center"
      style={{ aspectRatio }}
    >
      <div className="w-5 h-5 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      <span className="absolute bottom-1 text-xxs text-foreground/30">
        Generating...
      </span>
    </div>
  )
})

PendingCell.displayName = 'PendingCell'

export const ImageHistoryPopover = memo(function ImageHistoryPopover({
  projectId,
  history,
  pickedIndex,
  onSelect,
  isOpen,
  thumbnailWidth,
  cacheKey,
  batchPending = 0,
  cellAspectRatio = '16/9',
}: ImageHistoryPopoverProps) {
  const [currentPage, setCurrentPage] = useState(0)
  const [fullscreenImagePath, setFullscreenImagePath] = useState<string | null>(
    null
  )

  const visibleHistoryCount = useMemo(
    () => history.filter((entry) => entry?.result != null).length,
    [history],
  )
  const totalItems = visibleHistoryCount + batchPending
  const columns = Math.min(
    thumbnailWidth > 400 ? 2 : 3,
    Math.max(totalItems, 1)
  )
  const rows = 3
  const itemsPerPage = columns * rows
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  type DisplayItem =
    | { kind: 'pending'; key: string }
    | { kind: 'entry'; entry: HistoryEntry; realIdx: number }

  const displayItems = useMemo<DisplayItem[]>(() => {
    const items: DisplayItem[] = []
    for (let i = 0; i < batchPending; i++) {
      items.push({ kind: 'pending', key: `pending-${i}` })
    }
    for (let i = history.length - 1; i >= 0; i--) {
      const entry = history[i]
      if (!entry?.result) continue
      items.push({ kind: 'entry', entry, realIdx: i })
    }
    return items
  }, [history, batchPending])

  const pageItems = useMemo(() => {
    const start = currentPage * itemsPerPage
    return displayItems.slice(start, start + itemsPerPage)
  }, [displayItems, currentPage, itemsPerPage])

  // Reset page if it goes out of bounds
  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(0)
    }
  }, [currentPage, totalPages])

  if (!isOpen) return null

  const handlePrevPage = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (currentPage > 0) setCurrentPage(currentPage - 1)
  }

  const handleNextPage = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1)
  }

  const handleDownload = async (e: React.MouseEvent, imagePath: string) => {
    e.stopPropagation()
    e.preventDefault()
    const url = getAssetUrl(projectId, imagePath)
    if (!url) return

    try {
      const response = await authenticatedFetch(url)
      if (!response.ok) {
        toast.error(`Download failed: ${response.status} ${response.statusText}`)
        return
      }
      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = imagePath.split('/').pop() || 'image.jpg'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(objectUrl)
    } catch (err) {
      console.error('Download failed', err)
      toast.error('Download failed — check network connection')
    }
  }

  const handleExpand = (e: React.MouseEvent, imagePath: string) => {
    e.stopPropagation()
    e.preventDefault()
    setFullscreenImagePath(imagePath)
  }

  const gap = 4
  const padding = 8
  const popoverWidth =
    columns * thumbnailWidth + (columns - 1) * gap + padding * 2

  return (
    <>
      <div
        role="dialog"
        aria-label="Image generation history"
        data-testid="image-history-popover"
        className="nopan nodrag absolute z-50"
        style={{
          left: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          marginLeft: 8,
          width: popoverWidth,
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {/* Grid */}
          <div
            className="p-2"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap: `${gap}px`,
            }}
          >
            {pageItems.map((item) => {
              if (item.kind === 'pending') {
                return (
                  <PendingCell
                    key={item.key}
                    aspectRatio={cellAspectRatio}
                  />
                )
              }

              const { entry, realIdx } = item
              const isSelected = realIdx === pickedIndex
              const imagePath = entry.result?.local_path
              const thumbUrl = imagePath
                ? getThumbnailUrl(
                    projectId,
                    imagePath,
                    thumbnailWidth,
                    cacheKey
                  )
                : null

              return (
                <div
                  key={entry.result?.id || realIdx}
                  data-testid="popover-history-item"
                  className={`group relative rounded overflow-hidden border-2 transition-colors ${
                    isSelected
                      ? 'border-yellow-400/50'
                      : 'border-transparent hover:border-border'
                  }`}
                  style={{ aspectRatio: cellAspectRatio }}
                >
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt={`Version ${realIdx + 1}`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full bg-black/30 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-foreground/10"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14"
                        />
                      </svg>
                    </div>
                  )}

                  {/* Download + Fullscreen overlay buttons */}
                  {thumbUrl && imagePath && (
                    <div
                      data-testid="popover-cell-overlay-buttons"
                      className="absolute top-1 left-1 z-10 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <button
                        data-testid="popover-expand-btn"
                        onClick={(e) => handleExpand(e, imagePath)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="w-5 h-5 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                        title="Expand"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                          />
                        </svg>
                      </button>
                      <button
                        data-testid="popover-download-btn"
                        onClick={(e) => handleDownload(e, imagePath)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="w-5 h-5 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                        title="Download"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Selection UI */}
                  <div className="absolute top-1 right-1 z-10">
                    {isSelected ? (
                      <span
                        data-testid="image-cell-selected-star"
                        className="text-yellow-400 text-xl leading-none"
                        title="Currently selected"
                      >
                        &#9733;
                      </span>
                    ) : (
                      <button
                        data-testid="image-cell-choose-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelect(realIdx)
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        className="text-xxs font-medium text-foreground bg-black/80 hover:bg-black/90 px-1.5 py-0.5 rounded transition-colors cursor-pointer"
                      >
                        Choose v{realIdx + 1}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 py-2 border-t border-border">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                aria-label="Previous page"
                aria-disabled={currentPage === 0}
                className={`p-1 rounded transition-colors ${
                  currentPage === 0
                    ? 'text-foreground/20 cursor-default'
                    : 'text-text-secondary hover:text-foreground hover:bg-surface-2'
                }`}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <span className="text-xs text-foreground/50">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages - 1}
                aria-label="Next page"
                aria-disabled={currentPage === totalPages - 1}
                className={`p-1 rounded transition-colors ${
                  currentPage === totalPages - 1
                    ? 'text-foreground/20 cursor-default'
                    : 'text-text-secondary hover:text-foreground hover:bg-surface-2'
                }`}
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen modal */}
      {fullscreenImagePath &&
        createPortal(
          <div
            data-testid="popover-fullscreen-modal"
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setFullscreenImagePath(null)}
          >
            <button
              data-testid="popover-fullscreen-close"
              onClick={() => setFullscreenImagePath(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-foreground/70 hover:text-foreground flex items-center justify-center backdrop-blur-sm transition-colors z-10"
              title="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div
              className="relative max-w-full max-h-full p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={
                  getAssetUrl(
                    projectId,
                    fullscreenImagePath,
                    0,
                    cacheKey
                  ) || ''
                }
                alt="Full size preview"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>

            <button
              data-testid="popover-fullscreen-download"
              onClick={(e) => handleDownload(e, fullscreenImagePath)}
              className="absolute bottom-6 right-6 py-2.5 px-4 bg-surface-2 hover:bg-border text-foreground text-sm font-medium rounded-lg transition-colors flex items-center gap-2 backdrop-blur-sm"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download
            </button>
          </div>,
          document.body
        )}
    </>
  )
})

ImageHistoryPopover.displayName = 'ImageHistoryPopover'
